import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Send, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { DailyBriefsSkeleton } from "@/components/PageSkeletons";
import { fetchers, qk, prefetchDailyBriefDetail } from "@/lib/queryClient";
import { useEditorialProgress } from "@/hooks/useEditorialProgress";
import NextBriefWidget from "@/components/NextBriefWidget";
import PipelineHealthBadge from "@/components/PipelineHealthBadge";
import WingmanBriefs from "@/pages/WingmanBriefs";
import { McpBadge } from "@/pages/DemiBrief";

/*
 * Turn CC · 13 — Legacy v1 generate/regenerate buttons removed. Daily
 * briefs are now generated exclusively by the pipeline_v2 cron
 * (05:00 SGT). This page is a read-only historical browser:
 *  - v2 rows carry a discreet `v2` badge (right-hand side).
 *  - v1 rows carry a `LEGACY` badge next to it. Both link into
 *    /daily-briefs/:id which detects the pipeline_version and renders
 *    the appropriate branch.
 */

const STATUS_COLOR = {
  draft:     "border-hm-border bg-hm-bg-1 text-hm-fg-muted",
  running:   "border-transparent bg-hm-accent text-white",
  review:    "border-transparent bg-hm-accent-soft text-hm-accent-dim",
  completed: "border-transparent bg-hm-success/10 text-hm-success",
  empty:     "border-hm-border bg-hm-bg-1 text-hm-fg-muted italic",
  approved:  "border-transparent bg-hm-accent-dim text-white",
  sent:      "border-hm-border bg-hm-bg-2 text-hm-fg-muted",
  failed:    "border-transparent bg-hm-danger/10 text-hm-danger",
};

function StatusChip({ status }) {
  const cls = STATUS_COLOR[status] || STATUS_COLOR.draft;
  return (
    <span
      data-testid={`brief-status-${status}`}
      className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${cls}`}
    >
      {status}
    </span>
  );
}

function RegionsBar({ stats }) {
  const total = Object.values(stats || {}).reduce((a, b) => a + Number(b || 0), 0);
  if (!total) return <span className="mono text-[10px] text-hm-fg-subtle">—</span>;
  const parts = Object.entries(stats).map(([k, v]) => ({
    region: k,
    n: Number(v),
    pct: Math.max(4, Math.round((Number(v) / total) * 100)),
  }));
  return (
    <div className="flex items-center gap-1 min-w-[140px]">
      {parts.map((p) => (
        <div
          key={p.region}
          title={`${p.region}: ${p.n}`}
          style={{ width: `${p.pct}%` }}
          className="h-1.5 rounded-full bg-hm-accent-dim/70"
        />
      ))}
    </div>
  );
}

function EditorialTransitionBanner() {
  const p = useEditorialProgress();
  if (!p.isRunning && !p.endedAt) return null;
  const label = p.isRunning
    ? `Editorial layer running · ${p.counters.proposals_drafted} proposals drafted so far`
    : `Editorial layer complete · ${p.counters.proposals_drafted} proposal${p.counters.proposals_drafted === 1 ? "" : "s"}`;
  return (
    <button
      type="button"
      onClick={p.restore}
      className="w-full mb-4 flex items-center gap-3 rounded-md px-4 py-3 text-left cursor-pointer bg-transparent border"
      style={{ background: "var(--hm-accent-soft)", borderColor: "var(--hm-border)" }}
      data-testid="editorial-banner-dailybriefs"
    >
      <span
        className="mono uppercase tracking-widest"
        style={{ fontSize: 10, color: "var(--hm-accent-dim)" }}
      >
        Editorial
      </span>
      <span className="text-hm-fg flex-1 min-w-0 truncate" style={{ fontSize: 13 }}>
        {label}
      </span>
      <span className="mono text-hm-accent" style={{ fontSize: 11 }}>
        view progress →
      </span>
    </button>
  );
}

function VersionBadge({ pipelineVersion }) {
  const pv = (pipelineVersion || "").toLowerCase();
  const isV2 = pv.startsWith("v2");
  const isMcp = pv.startsWith("claude_pro_mcp");
  if (isMcp) {
    return (
      <span
        className="inline-flex items-center gap-1 justify-self-end rounded border px-1.5 py-0.5 mono uppercase tracking-widest text-[10px]"
        style={{
          background: "var(--hm-accent-soft)",
          borderColor: "var(--hm-accent-dim)",
          color: "var(--hm-accent)",
        }}
        data-testid="brief-row-version-badge-mcp"
        title="Pushed by Claude Pro via the Hail Mary MCP connector"
      >
        <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
        mcp
      </span>
    );
  }
  if (isV2) {
    return (
      <span
        className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border justify-self-end"
        style={{
          background: "var(--hm-bg-elev-1)",
          borderColor: "var(--hm-border)",
          color: "rgba(237,237,237,0.6)",
        }}
        data-testid="brief-row-version-badge-v2"
        title="Rendered by pipeline_v2"
      >
        v2
      </span>
    );
  }
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border justify-self-end"
      style={{
        background: "var(--hm-bg-elev-1)",
        borderColor: "var(--hm-border)",
        color: "rgba(237,237,237,0.5)",
      }}
      data-testid="brief-row-version-badge-legacy"
      title="Legacy v1 brief — read-only historique"
    >
      legacy
    </span>
  );
}

export default function DailyBriefsPage() {
  const [feed, setFeed] = useState("hailmary");

  // Query only when the active tab needs it. Hail Mary → source=hailmary,
  // Demi → source=demi (claude_pro_mcp imported_draft only). Wingman is a
  // separate feed with its own component / API.
  const briefSource = feed === "demi" ? "demi" : "hailmary";
  const { data, isPending } = useQuery({
    queryKey: qk.dailyBriefs({ source: briefSource }),
    queryFn: () => fetchers.dailyBriefs({ source: briefSource }),
    enabled: feed !== "wingman",
    refetchInterval: 10_000,
  });

  return (
    <div data-testid="daily-briefs-page">
      <PageHeader
        title="Daily Briefs"
        badge="Live"
        subtitle="Auto-generated by pipeline_v2 every morning at 05:00 SGT. Reviewed by you, pushed to Beyond Activ."
      />

      <div
        role="tablist"
        aria-label="Brief source"
        data-testid="brief-feed-toggle"
        className="inline-flex items-center gap-1 rounded-lg border border-hm-border bg-hm-bg-1 p-1 mb-5"
      >
        {[
          { id: "hailmary", label: "Hail Mary" },
          { id: "wingman",  label: "Wingman" },
          { id: "demi",     label: "Demi", mcp: true },
        ].map((t) => {
          const active = feed === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`brief-feed-tab-${t.id}`}
              onClick={() => setFeed(t.id)}
              className={`mono uppercase tracking-widest text-[11px] px-3 py-1.5 rounded-md transition-colors inline-flex items-center gap-1.5 ${
                active
                  ? "bg-hm-accent-soft text-hm-accent-dim"
                  : "text-hm-fg-muted hover:text-hm-fg"
              }`}
            >
              {t.label}
              {t.mcp && (
                <Sparkles
                  className="h-3 w-3"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>

      {feed === "wingman" ? (
        <WingmanBriefs />
      ) : feed === "demi" ? (
        <>
          {isPending || !data ? (
            <DailyBriefsSkeleton rows={4} />
          ) : data.data.length === 0 ? (
            <div
              className="hm-surface p-10 text-center"
              data-testid="briefs-empty-demi"
            >
              <Sparkles className="h-8 w-8 text-hm-fg-subtle mx-auto mb-3" />
              <div className="text-hm-fg font-medium">
                No Demi briefs yet
              </div>
              <p className="text-sm text-hm-fg-muted mt-2 max-w-md mx-auto">
                Push a brief from Claude.ai through the Hail Mary MCP connector
                using the <code className="mono text-xs">push_brief</code> tool.
                See <code className="mono text-xs">/app/memory/CLAUDE_PRO_MCP_SETUP.md</code>.
              </p>
            </div>
          ) : (
            <div className="hm-surface divide-y divide-hm-border" data-testid="briefs-list-demi">
              {data.data.map((b) => (
                <Link
                  key={b.id}
                  to={`/daily-briefs/${b.id}`}
                  onMouseEnter={() => prefetchDailyBriefDetail(b.id)}
                  onFocus={() => prefetchDailyBriefDetail(b.id)}
                  data-testid={`brief-row-${b.id}`}
                  className="grid grid-cols-[110px_120px_1fr_64px_20px] items-center gap-4 px-5 py-4 hover:bg-hm-bg-1/50 transition-colors"
                >
                  <div className="mono text-sm text-hm-fg">{b.brief_date}</div>
                  <StatusChip status={b.status} />
                  <div className="text-sm text-hm-fg-muted truncate">
                    Pushed via <span className="mono">push_brief</span>
                  </div>
                  <VersionBadge pipelineVersion={b.pipeline_version} />
                  <ChevronRight className="h-4 w-4 text-hm-fg-subtle" />
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <EditorialTransitionBanner />

          <div className="flex items-center justify-end mb-2" data-testid="pipeline-health-row">
            <PipelineHealthBadge />
          </div>

          <NextBriefWidget lastBrief={data?.data?.[0]} />

          {isPending || !data ? (
            <DailyBriefsSkeleton rows={8} />
          ) : data.data.length === 0 ? (
            <div className="hm-surface p-10 text-center" data-testid="briefs-empty">
              <Send className="h-8 w-8 text-hm-fg-subtle mx-auto mb-3" />
              <div className="text-hm-fg font-medium">No briefs yet</div>
              <p className="text-sm text-hm-fg-muted mt-2 max-w-md mx-auto">
                The pipeline_v2 cron will draft one automatically at 05:00 Asia/Singapore.
              </p>
            </div>
          ) : (
            <div className="hm-surface divide-y divide-hm-border" data-testid="briefs-list">
              {data.data.map((b) => (
                <Link
                  key={b.id}
                  to={`/daily-briefs/${b.id}`}
                  onMouseEnter={() => prefetchDailyBriefDetail(b.id)}
                  onFocus={() => prefetchDailyBriefDetail(b.id)}
                  data-testid={`brief-row-${b.id}`}
                  className="grid grid-cols-[110px_100px_60px_1fr_64px_20px] items-center gap-4 px-5 py-4 hover:bg-hm-bg-1/50 transition-colors"
                >
                  <div className="mono text-sm text-hm-fg">{b.brief_date}</div>
                  <StatusChip status={b.status} />
                  <div className="mono text-sm text-hm-fg-muted">{b.article_count}</div>
                  <RegionsBar stats={b.region_stats} />
                  <VersionBadge pipelineVersion={b.pipeline_version} />
                  <ChevronRight className="h-4 w-4 text-hm-fg-subtle" />
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
